import { useRef, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, ArrowUp, Loader2, Cpu, CheckCircle2, Bookmark, Mic, ChevronRight, ArrowRight,
  FilePenLine, FileText, Layers, BarChart3, CircleDollarSign, Wrench, ShieldCheck, ClipboardCheck, Lock,
} from "lucide-react";
import { streamWorkspace } from "@/lib/adaptiveStream";
import { WorkspaceComponent } from "@/components/adaptive/AdaptiveComponents";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const STEPS = [
  { n: 1, icon: FilePenLine, title: "Tell us your goal", sub: "Describe what you want to accomplish" },
  { n: 2, icon: FileText, title: "Get your plan", sub: "We show you exactly what to improve" },
  { n: 3, icon: CheckCircle2, title: "Take the next steps", sub: "Complete tasks and track your progress" },
];

const START_CARDS = [
  { icon: Layers, title: "Get Ready for Funding", sub: "See what you need to qualify", prompt: "Get my company ready for funding — what do I need to qualify?", goal: "prepare_100k" },
  { icon: BarChart3, title: "Improve Business Credit", sub: "Build a stronger credit profile", prompt: "Help me build and improve my business credit", goal: "build_biz_credit" },
  { icon: CircleDollarSign, title: "Understand Cash Flow", sub: "See where your money is going", prompt: "Explain my cash flow — where is my money going and what changed?" },
  { icon: Wrench, title: "Finance Equipment", sub: "Explore equipment funding options", prompt: "Prepare my company to finance new equipment", goal: "buy_equipment" },
  { icon: ShieldCheck, title: "Fix a Compliance Issue", sub: "Resolve problems holding you back", prompt: "What compliance or identity issues are holding me back, and how do I fix them?" },
  { icon: ClipboardCheck, title: "Review My Next Steps", sub: "Continue your personalized plan", prompt: "Review my personalized action plan and top next steps" },
];

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export default function AdaptiveOS() {
  const { user } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | streaming | done
  const [intent, setIntent] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [components, setComponents] = useState([]);
  const abortRef = useRef(null);
  const [params] = useSearchParams();
  const [saveName, setSaveName] = useState("");

  const firstName = (user?.name || "there").split(" ")[0];

  const run = async (text, goalId = null) => {
    const q = (text ?? prompt).trim();
    if (!q || phase === "streaming") return;
    setPrompt(q);
    setPhase("streaming");
    setIntent(null);
    setStatuses([]);
    setComponents([]);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamWorkspace({
        prompt: q,
        goalId,
        signal: controller.signal,
        onEvent: (ev) => {
          if (ev.type === "intent") setIntent(ev);
          else if (ev.type === "status") setStatuses((s) => [...s, { text: ev.text, agent: ev.agent, done: false }]);
          else if (ev.type === "component") {
            setComponents((c) => [...c, ev.component]);
            setStatuses((s) => s.map((x, i) => (i === s.length - 1 ? { ...x, done: true } : x)));
          } else if (ev.type === "done") setPhase("done");
        },
      });
      setPhase("done");
    } catch {
      setPhase("done");
    }
  };

  const started = phase !== "idle";

  useEffect(() => {
    const p = params.get("prompt");
    if (p) run(p, params.get("goal"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveCurrent = async () => {
    const name = saveName.trim() || (intent ? `${intent.intent} workspace` : "My workspace");
    try {
      await api.post("/workspaces", { name, prompt, goal_id: params.get("goal") || (intent && intent.goal_id) });
      toast.success("Workspace saved");
      setSaveName("");
    } catch { toast.error("Could not save workspace"); }
  };

  // ---------- IDLE: personalized home ----------
  if (!started) {
    return (
      <div className="mx-auto max-w-5xl animate-fade-up pb-10" data-testid="adaptive-os-page">
        <p className="text-[15px] font-medium text-slate-500">{greeting()}, {firstName}.</p>
        <h1 className="mt-1 font-head text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
          What would you like <span className="text-foil">help with</span> today?
        </h1>
        <p className="mt-3 max-w-2xl text-base text-slate-500">
          Tell us your business goal. We'll review your information and create a personalized action plan.
        </p>

        {/* Goal box */}
        <div className="card-spotlight mt-6 flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm sm:flex-row sm:items-center">
          <div className="flex flex-1 items-center gap-3 px-3">
            <Sparkles size={20} className="shrink-0 text-[#1a5fd0]" />
            <input
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); run(); } }}
              placeholder="Help me qualify for $100,000 in business funding"
              data-testid="adaptive-intent-input"
              className="h-12 flex-1 bg-transparent text-[15px] text-slate-900 outline-none placeholder:text-slate-400"
            />
            <button
              onClick={() => toast("Voice input is coming soon", { description: "Type your goal for now." })}
              className="shrink-0 rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-[#1a5fd0]"
              data-testid="adaptive-mic-btn" aria-label="Voice input">
              <Mic size={18} />
            </button>
          </div>
          <button
            onClick={() => run()}
            disabled={!prompt.trim()}
            data-testid="adaptive-run-btn"
            className="flex h-12 items-center justify-center gap-2 rounded-xl bg-[#1a5fd0] px-6 font-head text-[15px] font-bold text-white transition-all hover:bg-[#1550b8] disabled:opacity-40">
            Create My Plan <ChevronRight size={18} />
          </button>
        </div>

        {/* 3-step explainer */}
        <div className="mt-9 flex items-start justify-center gap-2 sm:gap-4">
          {STEPS.map((s, i) => (
            <div key={s.n} className="flex flex-1 items-start">
              <div className="flex flex-1 flex-col items-center text-center">
                <div className="mb-3 flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#1a5fd0]/10 font-head text-[12px] font-bold text-[#1a5fd0]">{s.n}</span>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#1a5fd0]/10 text-[#1a5fd0]"><s.icon size={20} /></span>
                </div>
                <div className="font-head text-[15px] font-bold text-slate-900">{s.title}</div>
                <div className="mt-1 max-w-[9rem] text-[12px] leading-snug text-slate-500">{s.sub}</div>
              </div>
              {i < STEPS.length - 1 && <div className="mt-[34px] hidden h-px flex-1 bg-slate-200 sm:block" />}
            </div>
          ))}
        </div>

        {/* Choose where to start */}
        <h2 className="mt-10 font-head text-lg font-extrabold text-slate-900">Or choose where to start</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {START_CARDS.map((c) => (
            <button key={c.title} onClick={() => run(c.prompt, c.goal)} data-testid="adaptive-start-card"
              className="group card-spotlight flex items-center gap-3.5 rounded-xl border border-slate-200 bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:border-[#1a5fd0]/40 hover:shadow-md">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#1a5fd0]/10 text-[#1a5fd0]"><c.icon size={20} /></span>
              <div className="min-w-0 flex-1">
                <div className="font-head text-[14px] font-bold text-slate-900">{c.title}</div>
                <div className="mt-0.5 text-[12px] text-slate-500">{c.sub}</div>
              </div>
              <ChevronRight size={18} className="shrink-0 text-slate-300 transition-colors group-hover:text-[#1a5fd0]" />
            </button>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-center gap-2 text-[12px] text-slate-400">
          <Lock size={13} /> Your information is secure and actions always require your approval.
        </div>
      </div>
    );
  }

  // ---------- STREAMING / DONE: generative workspace ----------
  return (
    <div className="animate-fade-up" data-testid="adaptive-os-page">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-end gap-2">
          <Sparkles size={18} className="mb-2.5 shrink-0 text-[#1a5fd0]" />
          <textarea
            rows={1}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); run(); } }}
            placeholder="Describe your goal or ask a question"
            data-testid="adaptive-intent-input"
            className="flex-1 resize-none bg-transparent py-2 text-[15px] text-slate-900 outline-none placeholder:text-slate-400"
          />
          <button onClick={() => run()} disabled={phase === "streaming" || !prompt.trim()} data-testid="adaptive-run-btn"
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#1a5fd0] text-white transition-opacity disabled:opacity-40">
            {phase === "streaming" ? <Loader2 size={18} className="animate-spin" /> : <ArrowUp size={18} />}
          </button>
        </div>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[260px_1fr]">
        {/* Agent activity stream */}
        <div className="lg:sticky lg:top-4 lg:self-start">
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              <Cpu size={14} className="text-[#1a5fd0]" />
              <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-slate-500">Agent Activity</span>
            </div>
            {intent && (
              <div className="mb-3 rounded-lg bg-slate-50 px-3 py-2">
                <div className="font-mono text-[9px] uppercase tracking-wider text-slate-400">Detected intent</div>
                <div className="text-[12px] font-semibold text-slate-800">{intent.intent}</div>
              </div>
            )}
            <div className="space-y-2.5">
              {statuses.map((s, i) => (
                <div key={i} className="flex items-start gap-2" data-testid="adaptive-status-line">
                  {s.done ? <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-500" /> : <Loader2 size={14} className="mt-0.5 shrink-0 animate-spin text-[#1a5fd0]" />}
                  <div>
                    <div className="text-[12px] text-slate-700">{s.text}</div>
                    {s.agent && <div className="font-mono text-[9px] uppercase tracking-wider text-slate-400">{s.agent}</div>}
                  </div>
                </div>
              ))}
              {phase === "done" && <div className="pt-1 font-mono text-[10px] uppercase tracking-wider text-emerald-600">✓ Workspace ready</div>}
            </div>
          </div>
        </div>

        {/* Generative workspace */}
        <div className="grid grid-cols-6 gap-4 content-start">
          {phase === "done" && components.length > 0 && (
            <div className="col-span-6 flex flex-wrap items-center gap-2">
              <input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Name this workspace" data-testid="workspace-name-input"
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-[12px] outline-none focus:border-[#1a5fd0]" />
              <button onClick={saveCurrent} data-testid="save-workspace-btn"
                className="flex items-center gap-1.5 rounded-lg bg-[#080d1a] px-3 py-1.5 text-[12px] font-semibold text-white">
                <Bookmark size={13} /> Save workspace
              </button>
            </div>
          )}
          {components.map((c, i) => <WorkspaceComponent key={`${c.type}-${i}`} spec={c} />)}
          {components.length === 0 && phase === "streaming" && (
            <div className="col-span-6 flex items-center justify-center rounded-2xl border border-dashed border-slate-200 py-16 text-sm text-slate-400">
              <Loader2 size={18} className="mr-2 animate-spin text-[#1a5fd0]" /> Building your personalized plan…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
